/**
 * Recursively removes empty attributes from an object.
 * An attribute is considered empty if it is:
 * - null
 * - undefined
 * - an empty string
 * - an empty array
 * - an empty object
 * - an instance of String with an empty value
 * @param {any} value - The value to remove empty attributes from.
 * @returns {any} - The value with empty attributes removed.
 */
function removeEmptyAttributes(value) {
    const isEmpty = (v) =>
        v === null ||
        v === undefined ||
        v === "" ||
        (Array.isArray(v) && v.length === 0) || // Empty array
        (typeof v === "object" && Object.keys(v).length === 0) || // Empty object
        (v instanceof String && v.valueOf() === ""); // new String("")
    if (value && typeof value === "object" && !Array.isArray(value)) {
        const valueEntries = Object.entries(value)
            .map(([key, value]) => [key, removeEmptyAttributes(value)]) // Recursive call
            .filter(([_, value]) => !isEmpty(value));
        return Object.fromEntries(valueEntries);
    }
    return value;
}

/**
 * Checks if the object is falsy (is empty or all its attributes are falsy)
 * @param {any} object object to check
 * @returns {boolean} true if the object is falsy, false otherwise
 */
function isObjectFalsy(object) {
    return !object || (object instanceof Object && Object.values(object).every((value) => isObjectFalsy(value)));
}

/**
 * Recursively removes properties with null or undefined values from an object.
 * @param {object} obj - The object from which null or undefined properties should be removed.
 */
function removeNullProperties(obj) {
    for (const key in obj) {
        if (obj[key] === null || obj[key] === undefined) {
            delete obj[key];
        } else if (typeof obj[key] === "object") {
            removeNullProperties(obj[key]);
        }
    }
}

/**
 * Creates a shallow copy of the object, omitting the specified keys.
 * @param {object} obj - The source object.
 * @param {string[]} keys - An array of keys to omit from the object.
 * @returns {object} - A new object without the specified keys.
 */
function omit(obj, keys) {
    const result = { ...obj };
    for (const key of keys) {
        delete result[key];
    }
    return result;
}

module.exports = {
    isObjectFalsy,
    omit,
    removeEmptyAttributes,
    removeNullProperties,
};
